import { useMemo } from "react";

import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { DATE_PRESETS, isPresetValue } from "#app/lib/utils/date-presets";
import { formatDateShort } from "#app/lib/utils/format-date";
import type { FilterFieldType } from "#app/lib/filters/types";
import {
  buildQuickFilterOptions,
  type QuickFilterPools,
} from "./quick-inputs/quick-filter-options";
import { useQuickFilterDirectory } from "#app/lib/hooks/use-quick-filter-directory";

const NO_IDS: string[] = [];

/** The noun a collapsed multi-select summary counts in. */
const LIST_PLURALS: Partial<Record<FilterFieldType, string>> = {
  "entity-user": "members",
  "entity-assignee": "assignees",
  "entity-team": "teams",
  "issue-type": "types",
  workflow: "workflows",
  stage: "stages",
  "age-status": "statuses",
  role: "roles",
};

/**
 * One-line description of a quick filter's applied value, for the collapsed
 * section header. Null when the value is empty (the section renders no
 * summary line).
 */
export function useQuickFilterSummary(
  type: FilterFieldType,
  value: unknown,
): string | null {
  const workspace = useWorkspaceData();
  const teamIds = useMemo(
    () =>
      type === "entity-team" && Array.isArray(value)
        ? value.filter((id): id is string => typeof id === "string")
        : NO_IDS,
    [type, value],
  );
  const directory = useQuickFilterDirectory(teamIds);
  const pools = useMemo(() => ({ ...workspace, directory }), [workspace, directory]);
  return useMemo(() => summarizeFilterValue(type, value, pools), [type, value, pools]);
}

/**
 * The pure form of `useQuickFilterSummary`, for a caller describing many
 * values at once (a saved filter's whole snapshot) where one hook per value
 * cannot be called.
 *
 * A list value reads its names from the same option set the panel's rows,
 * chips and search use, so a summary can never name a value differently
 * from the row that set it. A single selection shows the value's own name;
 * multiples collapse to a "3 stages selected" count. An id the pools no
 * longer hold (a deactivated member) still counts.
 */
export function summarizeFilterValue(
  type: FilterFieldType,
  value: unknown,
  pools: QuickFilterPools,
): string | null {
  if (value == null || value === "") return null;

  const plural = LIST_PLURALS[type];
  if (plural) {
    const ids = Array.isArray(value) ? (value as unknown[]) : [];
    const labels = buildQuickFilterOptions(type, pools, undefined, value)
      .filter((option) => option.selected(value))
      .map((option) => option.label);
    const count = Math.max(ids.length, labels.length);
    if (count === 1 && labels[0]) return labels[0];
    return `${count} ${plural} selected`;
  }

  switch (type) {
    case "workflow-stage": {
      const v = value as { workflowIds?: string[]; stageIds?: string[] };
      const wfs = v.workflowIds ?? [];
      const sts = v.stageIds ?? [];
      const [firstWf] = wfs;
      const [firstStage] = sts;
      const wfPart = !firstWf
        ? null
        : wfs.length === 1
          ? (pools.workflowMap.get(firstWf)?.name ?? "1 workflow")
          : `${wfs.length} workflows`;
      const stPart = !firstStage
        ? null
        : sts.length === 1
          ? (pools.stages.find((s) => s.stageId === firstStage)?.name ?? "1 stage")
          : `${sts.length} stages`;
      // Either half can stand alone: scoped to one workflow the section is
      // stages only, and the workflow half is never written.
      if (wfPart && stPart) return `${wfPart} · ${stPart}`;
      return wfPart ?? stPart;
    }

    case "last-updated": {
      if (!isPresetValue(value)) return null;
      if (value.key === "custom" && value.custom) {
        return `${formatDateShort(value.custom.startDate)} – ${formatDateShort(value.custom.endDate)}`;
      }
      return DATE_PRESETS.find((p) => p.key === value.key)?.label ?? null;
    }

    case "more-options":
      return value === true ? "Archived only" : null;

    case "boolean":
      return value === true ? "True" : "False";

    case "date":
      return typeof value === "string" ? formatDateShort(value) : null;

    case "string":
    case "email":
    case "number":
      return String(value);

    default: {
      const ids = Array.isArray(value) ? (value as unknown[]) : [];
      return ids.length > 0 ? `${ids.length} selected` : String(value);
    }
  }
}
